import React, { createContext, useContext, useState, useMemo } from 'react';
import axios from 'axios';
import { usePanelContext } from './PanelContext';
import alertIcon from '../assets/icons/prediction-stroke.png';
import { useUserContext } from './UserContext.js';
import mapboxgl from 'mapbox-gl';
const PredictionsContext = createContext();

export const PredictionsContextProvider = ({ children }) => {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        setPanel
    } = usePanelContext(); 

    const [predictions, setPredictions] = useState(null);

    const plotPredictions = (map, data) => {
        const layers = map.current.getStyle().layers?.filter(layer => layer.id.includes('Layer-Prediction-symbol')) || [];

        if (layers.length > 0)
            layers.forEach((layer) => map.current.removeLayer(layer.id));

        const source = map.current.getStyle().sources['Source-Prediction'];

        if (source)
            map.current.removeSource('Source-Prediction');

        if (map.current.hasImage('prediction-icon')) {
            map.current.removeImage('prediction-icon');
        }

        map.current.loadImage(alertIcon, function(error, image) {
            if (error) throw error;

            map.current.addImage('prediction-icon', image);

            map.current.addSource(`Source-Prediction`,
                {
                    "type": "geojson",
                    "data": {
                        "type": "FeatureCollection",
                        "features": 
                        data.map((prediction) => {
                                return {
                                    "type": "Feature",
                                    "properties": prediction,
                                    "geometry": {
                                        "type": "Point",
                                        "coordinates": [prediction.longitude, prediction.latitude]
                                    }
                                }
                            })
                        
                    }
                }
            );

            map.current.addLayer({
                "id": `Layer-Prediction-symbol`,
                "type": "symbol",
                "source": `Source-Prediction`,
                "layout": {
                    "icon-image": "prediction-icon",
                    "icon-size": 0.6,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true
                }
            });

            const layers = map.current.getStyle().layers.filter((layer) => layer.id === 'RAWS Station Layer');

            if (layers.length > 0)
                map.current.moveLayer('RAWS Station Layer');

            map.current.on('click', 'Layer-Prediction-symbol', (e) => {
                const features = map.current.queryRenderedFeatures(e.point, {
                    layers: ['Layer-Prediction-symbol']
                });
            
                if (!features.length) {
                    return;
                }
            
                const feature = features[0];
                const props = feature.properties;
                const location = [props.longitude, props.latitude];

                // Create popup similar to fire popup
                const popup = new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                .setLngLat(location)
                .setHTML(`
                    <div>
                        <div class='alert-popup-title'>Prediction (${props.fire_id})</div>
                        <div class='alert-popup-row'><div class='alert-popup-row-name'>ID:</div><div class='alert-popup-row-value'>${props.fire_id}</div></div>
                        <div class='alert-popup-row'><div class='alert-popup-row-name'>Country:</div><div class='alert-popup-row-value'>${props.country || 'Not Available'}</div></div>
                        <div class='alert-popup-row'><div class='alert-popup-row-name'>Coordinates:</div><div class='alert-popup-row-value'>${location[1].toFixed(5)}, ${location[0].toFixed(5)}</div></div>
                        <div class='alert-popup-row-button js-view-prediction'>View Prediction</div>
                    </div>
                `)
                .addTo(map.current);

                const btn = popup.getElement().querySelector('.js-view-prediction');
                btn.style.cursor = 'pointer';
                btn.addEventListener('click', () => {
                    popup.remove();

                    setPanel({
                        name: 'Prediction PopUp',
                        content: props
                    });
            
                });
            });

            // Change the cursor to a pointer when over the layer
            map.current.on('mouseenter', 'Layer-Prediction-symbol', () => {
                map.current.getCanvas().style.cursor = 'pointer';
            });
        
            // Change it back to default when leaving the layer
            map.current.on('mouseleave', 'Layer-Prediction-symbol', () => {
                map.current.getCanvas().style.cursor = '';
            });
        });
    }

    const getPredictions = (map, token, setRefreshingCallback) => {
        if (setRefreshingCallback) {
            setRefreshingCallback(true);
        }

        axios.get('https://firewatch-api.space-eyes.com/predictions', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then((response) => {
            if (response.data.predictions.length > 0) {
                setPredictions(response.data.predictions);
                plotPredictions(map, response.data.predictions);
            } else {
                setPredictions([]);
                plotPredictions(map, []);
            }

            if (setRefreshingCallback){
                setRefreshingCallback(false);
            }
        }).catch((err) => {
            console.log('error gettting predictions', err);

            if (setRefreshingCallback){
                setRefreshingCallback(false);
            }
        })
    }

    const value = useMemo(() => ({
        predictions,
        setPredictions,
        getPredictions
    }), [predictions]);
  
    return (
        <PredictionsContext.Provider 
            value={value}
        >
            {children}
        </PredictionsContext.Provider>
    );
    };

export const usePredictionsContext = () => useContext(PredictionsContext);
